// Register / deliverables / ACs / test cases API surface for the operator SPA.
// Every call goes through the shared request helper — never raw fetch in components.
import { request } from './client.js';

// ───────────────────────── types ─────────────────────────

export type RequirementType = 'functional' | 'non_functional' | 'constraint' | 'assumption' | 'out_of_scope';
export type Priority = 'must' | 'should' | 'could' | 'wont';
export type RequirementStatus = 'draft' | 'confirmed' | 'baselined' | 'changed' | 'descoped' | 'delivered' | 'accepted';

export interface LintFlag {
  code: string;
  severity: 'error' | 'warn';
  message: string;
  term?: string;
}
export interface LintResult {
  flags: LintFlag[];
  pass: boolean;
}

export interface DeliverableRef {
  id: number;
  ref: string;
  name: string;
}

export interface RegisterRow {
  id: number;
  ref: string;
  type: RequirementType;
  priority: Priority;
  status: RequirementStatus;
  category: string | null;
  /** Engagement-specific classification, next to the category. Always present, often []. */
  tags: string[];
  statement: string | null;
  rationale: string | null;
  /** The CURRENT revision's id — what the translation sidecar keys on (the register's prose
   *  belongs to a revision, not to the requirement: a new revision needs its own translations
   *  and the predecessor keeps the ones it had). Always on the wire; declared here since <Tx>
   *  needs it. */
  revision_id: number | null;
  revision_no: number | null;
  revision_source: string | null;
  revised_at: string | null;
  created_at: string;
  evidence_count: number;
  ac_count: number;
  tc_count: number;
  ac_without_tc_count: number;
  deliverables: DeliverableRef[];
  source_document_refs: string[];
  coverage: { noAc: boolean; noDeliverable: boolean; acWithoutTc: boolean };
  locked: boolean;
  baselined: boolean;
  /** Set when this requirement was merged away — its content lives on in that ref. */
  merged_into_ref?: string | null;
}

export interface EvidenceRow {
  id: number;
  revision_id: number;
  document_id: number;
  document_ref: string;
  original_filename: string;
  effective_date: string | null;
  source_type: string;
  document_status: string;
  quoted_span: string;
  locator: { char_start?: number; char_end?: number; page?: number; speaker?: string } & Record<string, unknown>;
  confidence: number | null;
  created_at: string;
}

export interface RevisionRow {
  id: number;
  revision_no: number;
  statement: string;
  rationale: string | null;
  acceptance_notes: string | null;
  source: string;
  proposal_id: number | null;
  change_request_id: number | null;
  change_request_ref: string | null;
  supersedes_revision_id: number | null;
  created_at: string;
  created_by: number | null;
  created_by_name: string | null;
  created_by_kind: string;
  is_current: boolean;
  evidence: EvidenceRow[];
}

export interface AcceptanceCriterion {
  id: number;
  requirement_id?: number;
  requirement_ref?: string;
  requirement_type?: RequirementType;
  ref: string;
  seq: number;
  statement: string;
  measurement_method: string | null;
  lint_flags: LintResult | null;
  status: string;
  created_at: string;
  test_cases: { id: number; ref: string; title: string; status: string }[];
}

export interface TestCaseStep {
  n: number;
  action: string;
  expected: string;
}

export interface TestCase {
  id: number;
  engagement_id: number;
  ref: string;
  title: string;
  preconditions: string | null;
  test_data_ref: string | null;
  steps: TestCaseStep[];
  expected_result: string | null;
  status: string;
  created_at: string;
  acceptance_criteria: { id: number; ref: string; statement: string; requirement_id: number; requirement_ref: string }[];
}

/**
 * THE PENCIL LAYER — a working value ("Annahme") the operator plans with, tethered to the open
 * question that has to settle before it can become the record. It shadows one field of one row
 * and never writes it: the roadmap draws it as a ghost, the client call reverts or adopts it.
 */
export interface WorkingOverride {
  id: number;
  engagement_id: number;
  entity_type: 'deliverable' | 'deliverable_item';
  entity_id: number;
  /** The ref of the row this shadows — DEL-03, or DEL-03.2 for a content item. */
  entity_ref: string | null;
  entity_label?: string | null;
  field: 'due_date' | 'start_date';
  /** The pencilled date, ISO. */
  value: string;
  note: string | null;
  open_question_id: number;
  question_ref: string | null;
  question: string;
  question_status: string;
  /** What the RECORD says today — the faint slot the ghost floats away from. */
  record_value: string | null;
  created_at: string;
}

/** An open question a date can be pencilled against, as the deliverable payload computes them. */
export interface TetherQuestion {
  id: number;
  ref: string | null;
  question: string;
}

/**
 * One edge of the dependency linker: content in FROM builds on content in TO (optionally
 * pinned to a content item on either end). `stale` says the TO side's content moved since the
 * FROM side last confirmed — the "linked content changed" ribbon in the bench. Both ends'
 * refs ride along so the bench renders chips without a second fetch.
 */
export interface DeliverableLink {
  id: number;
  kind: 'depends_on';
  note: string | null;
  provenance: 'operator' | 'agent';
  stale: boolean;
  confirmed_at: string;
  created_at: string;
  from_deliverable_id: number;
  from_ref: string;
  from_name: string;
  from_item_id: number | null;
  from_item_ref: string | null;
  to_deliverable_id: number;
  to_ref: string;
  to_name: string;
  to_item_id: number | null;
  to_item_ref: string | null;
  to_item_title: string | null;
}

export interface Deliverable {
  id: number;
  engagement_id: number;
  ref: string;
  name: string;
  description: string | null;
  definition_of_done: string | null;
  status: string;
  start_date: string | null;
  due_date: string | null;
  /** The roadmap row the operator pinned this card to; null = auto-packed. */
  roadmap_lane: number | null;
  milestone_id: number | null;
  milestone_name: string | null;
  ac_count: number;
  created_at: string;
  requirements: {
    id: number; ref: string; priority: Priority; status: RequirementStatus; statement: string | null;
    /** An OPEN question names this requirement's ref — the ground under it is still being argued. */
    in_clarification?: boolean;
  }[];
  /** The assumptions standing on this deliverable's own dates. Always present, often []. */
  overrides?: WorkingOverride[];
  /** The open questions that name a requirement this deliverable satisfies — computed server-side. */
  open_questions?: TetherQuestion[];
  /** What THIS deliverable builds on — the linker's outbound edges. Always present, often []. */
  links?: DeliverableLink[];
  /** What builds on this deliverable — the inbound edges. Always present, often []. */
  linked_by?: DeliverableLink[];
}

/** One CONTENT ITEM a deliverable is worked out into (the workbench's unit). */
export interface DeliverableItem {
  id: number;
  engagement_id: number;
  deliverable_id: number;
  deliverable_ref?: string;
  /** DEL-01.2 — the parent's ref plus a per-deliverable sequence. Null only on pre-migration rows. */
  ref: string | null;
  seq: number | null;
  /** The assumptions standing on this item's due date. Always present, often []. */
  overrides?: WorkingOverride[];
  title: string;
  spec: string | null;
  due_date: string | null;
  status: 'draft' | 'held' | 'dropped';
  provenance: string | null;
  document_ids: number[];
  /** The requirements (by id) this piece serves — a subset of the deliverable's own mapping. */
  requirement_ids: number[];
  sort_order: number;
  created_at: string;
}

export interface RequirementDetail extends RegisterRow {
  engagement_id: number;
  engagement_code: string;
  acceptance_notes: string | null;
  current_revision_id: number | null;
  revisions: RevisionRow[];
  evidence: EvidenceRow[];
  acceptance_criteria: AcceptanceCriterion[];
  test_cases: TestCase[];
  change_requests: { id: number; ref: string; title: string; status: string; goodwill: boolean }[];
}

/**
 * The P2 rollup registerQueries.ts already ships and nothing rendered: how much of this register
 * cannot answer "why does it say this?". `warning` is the server's own finished sentence — the
 * SOURCED cell prints it unedited (B7) rather than composing a second version of it.
 */
export interface CoverageProvenance {
  without_evidence: number;
  confirmed_without_evidence: number;
  refs: string[];
  warning: string | null;
}

export interface CoverageReport {
  engagement_id: number;
  totals: Record<string, number>;
  requirements_without_deliverable: { id: number; ref: string; statement: string; priority: Priority }[];
  must_requirements_without_deliverable: { id: number; ref: string; statement: string }[];
  deliverables_without_requirement: { id: number; ref: string; name: string }[];
  requirements_without_ac: { id: number; ref: string; statement: string }[];
  requirements_without_evidence: { id: number; ref: string; statement: string }[];
  /** Already past draft and citing nothing — P2 violations that predate the confirm guard. */
  requirements_confirmed_without_evidence: { id: number; ref: string; statement: string }[];
  requirements_in_draft: { id: number; ref: string; statement: string }[];
  acs_without_test_case: { id: number; ref: string; statement: string; requirement_ref: string }[];
  acs_failing_lint: { id: number; ref: string; statement: string; requirement_ref: string; lint_flags: LintResult }[];
  provenance: CoverageProvenance;
  /**
   * Keyed G1/G2/G3 by `coverageReport`. NOTE: these letters are NOT `utils/gates.ts`'s letters —
   * coverage's "G3" means "every AC has a test case" while the gate engine's G3 is "SOW signed".
   * The register page therefore never prints a letter: it names the obligation and lets Lifecycle
   * own the gate ids (register IMPLEMENTATION §2 / FEASIBILITY F6.3).
   */
  gates: Record<string, { pass: boolean; blockers: string[] }>;
}

export interface EvidenceInput {
  document_id: number;
  quoted_span: string;
  locator?: Record<string, unknown>;
  confidence?: number | null;
}

// ───────────────────────── hard delete ─────────────────────────
//
// Retire and delete coexist. Descope / dropped keep a row whose history matters; delete is for
// the mistake. The plan below is the server's answer to "may this go, what goes with it, and
// if not, what should I do instead" — the UI shows it and never guesses.

export type DeletableEntity = 'requirement' | 'deliverable' | 'acceptance_criterion' | 'test_case';

export interface DeletionBlocker {
  code: string;
  /** Reads to the operator and names the alternative. Show it verbatim. */
  message: string;
}

/**
 * One place in the register's prose that names a ref — derived from the text on the server
 * (utils/proseReferences.ts), never stored, so it can never drift from what is written.
 */
export interface ProseReference {
  entity_type: 'deliverable' | 'deliverable_item' | 'requirement' | 'acceptance_criterion' | 'test_case' | 'open_question';
  entity_id: number;
  ref: string | null;
  label: string | null;
  field: string;
  excerpt: string;
  /** For items: the parent deliverable — the bench a chip should open. */
  deliverable_id: number | null;
}

export interface DeletionPlan {
  allowed: boolean;
  blockers: DeletionBlocker[];
  cascade: { what: string; count: number }[];
  label: string;
  entity: DeletableEntity;
  id: number;
  ref: string | null;
  engagement_id: number | null;
  /** Prose elsewhere that names this ref — it stays behind and dangles; its own dialog line. */
  prose_references?: number;
}

export interface DeletionResult {
  deleted: true;
  entity: DeletableEntity;
  id: number;
  ref: string | null;
  label: string;
  cascade: { what: string; count: number }[];
}

export interface BulkDeleteResult {
  dry_run: boolean;
  deleted: { id: number; ref: string | null; label: string; cascade: { what: string; count: number }[] }[];
  refused: { id: number; ref: string | null; label: string; reason: string; code: string }[];
  missing: number[];
}

/** The policy names cascade rows in the plural; a count of one deserves the singular. */
const SINGULAR: Record<string, string> = {
  revisions: 'revision',
  'evidence links': 'evidence link',
  'acceptance criteria': 'acceptance criterion',
  'deliverable mappings': 'deliverable mapping',
  'requirement mappings': 'requirement mapping',
  'acceptance-record links': 'acceptance-record link',
  'acceptance-criterion links': 'acceptance-criterion link',
  'test-case links': 'test-case link',
};

/**
 * "3 revisions, 2 evidence links and 2 acceptance criteria go with it" — the confirm dialog
 * says what leaves in words, not as a JSON dump of the plan.
 */
export function cascadeSentence(cascade: { what: string; count: number }[]): string | null {
  const rows = cascade.filter((c) => c.count > 0);
  if (!rows.length) return null;
  const parts = rows.map((c) => `${c.count} ${c.count === 1 ? (SINGULAR[c.what] ?? c.what) : c.what}`);
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  const verb = rows.length === 1 && rows[0].count === 1 ? 'goes' : 'go';
  return `${list} ${verb} with it.`;
}

/** Server-side P2 verdict on one evidence row: index into the submitted array + why. */
export interface EvidenceFailure {
  index: number;
  reason: string;
}

/**
 * The 422 the register throws when a quoted span is not verbatim. The shared request()
 * helper flattens an error body to its message, which loses the per-row `failures` — so
 * create/revise re-ask the dry-run validator and rethrow this, letting the form put each
 * reason against the input that caused it instead of one generic banner.
 */
export class EvidenceInvalidError extends Error {
  failures: EvidenceFailure[];
  constructor(message: string, failures: EvidenceFailure[]) {
    super(message);
    this.name = 'EvidenceInvalidError';
    this.failures = failures;
  }
}

const looksLikeEvidenceFailure = (err: unknown): boolean =>
  err instanceof Error && /evidence|quoted_span/i.test(err.message);

/** Run a mutation; on an evidence rejection, recover the per-row reasons and rethrow them. */
async function withEvidenceDiagnostics<T>(
  engagementId: number | undefined,
  evidence: EvidenceInput[] | undefined,
  send: () => Promise<T>,
): Promise<T> {
  try {
    return await send();
  } catch (err) {
    if (!engagementId || !evidence?.length || !looksLikeEvidenceFailure(err)) throw err;
    const check = await request<{ ok: boolean; failures?: EvidenceFailure[] }>(
      '/register/validate-evidence',
      json({ engagement_id: engagementId, evidence }),
    ).catch(() => null);
    if (check && !check.ok && check.failures?.length) {
      throw new EvidenceInvalidError((err as Error).message, check.failures);
    }
    throw err;
  }
}

export interface SpanContext {
  document_id: number;
  document_ref: string;
  original_filename?: string;
  effective_date?: string | null;
  char_start: number;
  char_end: number;
  before: string;
  span: string;
  after: string;
  truncated_before?: boolean;
  truncated_after?: boolean;
}

export interface CategorySchemeEntry {
  name: string;
  description: string;
}

/** The universal category scheme — fixed in code, identical for every engagement. */
export interface CategorySchemeResponse {
  engagement_id: number;
  source: 'universal';
  entries: CategorySchemeEntry[];
}

/** The tag vocabulary the AGENT may use. null = unset (the agent proposes no tags). */
export interface TagPoolResponse {
  engagement_id: number;
  pool: string[] | null;
}

export interface RegisterFilters {
  type?: string;
  priority?: string;
  status?: string;
  category?: string;
  deliverable_id?: number;
  document_id?: number;
  q?: string;
  coverage_gap?: boolean;
  include_descoped?: boolean;
}

// ───────────────────────── helpers ─────────────────────────

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });
const patch = (body: unknown): RequestInit => ({ method: 'PATCH', body: JSON.stringify(body) });
const put = (body: unknown): RequestInit => ({ method: 'PUT', body: JSON.stringify(body) });
const del = (): RequestInit => ({ method: 'DELETE' });

// ───────────────────────── register ─────────────────────────

export interface OpenQuestion {
  id: number;
  ref: string | null;
  question: string;
  context: string | null;
  status: 'open' | 'resolved' | 'dropped';
  resolution: string | null;
  raised_by_kind: 'operator' | 'agent' | 'portal';
  created_at: string;
  document_ref: string | null;
}

/**
 * What settling a question answers with. `assumptions_reverted` is what the toast may honestly
 * claim; `assumptions` is non-empty only when the caller asked to HOLD them (the Amend path),
 * and is then the exact list the adopt step writes through the entities' own PATCH routes.
 */
export interface QuestionSettlement extends OpenQuestion {
  assumptions: WorkingOverride[];
  assumptions_reverted: number;
  reverted_refs: string[];
}

export const registerApi = {
  openQuestions: (engagementId: number) =>
    request<OpenQuestion[]>(`/register/open-questions${qs({ engagement_id: engagementId })}`),

  /**
   * Settle a question — and, with it, the assumptions tethered to it.
   * `assumptions: 'revert'` (the default) deletes them: the record stands. `'hold'` keeps them
   * and hands them back, for the Amend path's one-click adopt.
   */
  updateOpenQuestion: (
    id: number,
    engagementId: number,
    status: 'resolved' | 'dropped' | 'open',
    resolution?: string,
    opts: { assumptions?: 'revert' | 'hold'; call_ref?: string } = {},
  ) =>
    request<QuestionSettlement>(`/register/open-questions/${id}`, {
      method: 'POST',
      body: JSON.stringify({ engagement_id: engagementId, status, resolution, ...opts }),
    }),

  // ── the pencil layer ──
  overrides: (engagementId: number) =>
    request<WorkingOverride[]>(`/register/overrides${qs({ engagement_id: engagementId })}`),

  /** Pencil a date in, or move one already pencilled. One row per (entity, field). */
  setOverride: (
    engagementId: number,
    body: {
      entity_type: 'deliverable' | 'deliverable_item';
      entity_id: number;
      field: 'due_date' | 'start_date';
      value: string;
      open_question_id: number;
      note?: string | null;
    },
  ) => request<WorkingOverride>('/register/overrides', put({ engagement_id: engagementId, ...body })),

  /** Rub one out. The record never moved, so there is nothing to undo on it. */
  revertOverride: (id: number, engagementId: number) =>
    request<{ deleted: boolean; id: number; assumptions_reverted: number }>(
      `/register/overrides/${id}${qs({ engagement_id: engagementId })}`,
      del(),
    ),

  /**
   * The second half of Amend: the caller has already written the real dates through the
   * deliverable's / item's own PATCH routes; this clears the pencil and records the adoption.
   */
  adoptOverrides: (engagementId: number, openQuestionId: number, callRef?: string) =>
    request<{ adopted: number; refs: string[] }>(
      '/register/overrides/adopt',
      json({ engagement_id: engagementId, open_question_id: openQuestionId, call_ref: callRef }),
    ),

  list: (engagementId: number, filters: RegisterFilters = {}) =>
    request<{ engagement_id: number; count: number; rows: RegisterRow[] }>(
      `/register${qs({ engagement_id: engagementId, ...filters })}`,
    ),

  get: (id: number) => request<RequirementDetail>(`/register/${id}`),

  create: (
    engagementId: number,
    body: {
      statement: string;
      rationale?: string;
      acceptance_notes?: string;
      type?: RequirementType;
      priority?: Priority;
      category?: string;
      evidence?: EvidenceInput[];
    },
  ) =>
    withEvidenceDiagnostics(engagementId, body.evidence, () =>
      request<RequirementDetail>('/register', json({ engagement_id: engagementId, ...body })),
    ),

  revise: (
    id: number,
    body: {
      statement?: string;
      rationale?: string;
      acceptance_notes?: string;
      type?: RequirementType;
      priority?: Priority;
      category?: string;
      change_request_id?: number | null;
      evidence?: EvidenceInput[];
    },
    /** Needed only to re-ask the evidence validator when the server rejects a span. */
    engagementId?: number,
  ) =>
    withEvidenceDiagnostics(engagementId, body.evidence, () =>
      request<RequirementDetail>(`/register/${id}/revise`, json(body)),
    ),

  setStatus: (id: number, status: RequirementStatus) =>
    request<RequirementDetail>(`/register/${id}/status`, json({ status })),

  descope: (id: number, reason?: string) => request<RequirementDetail>(`/register/${id}/descope`, json({ reason })),
  /** the way back from descoped — free before the first baseline, a change request after */
  restore: (id: number, changeRequestId?: number) =>
    request<RequirementDetail>(`/register/${id}/restore`, json({ change_request_id: changeRequestId ?? null })),

  merge: (engagementId: number, sourceId: number, targetId: number, mergedStatement?: string) =>
    request<{ target: RequirementDetail; source_ref: string; target_ref: string }>(
      '/register/merge',
      json({ engagement_id: engagementId, source_id: sourceId, target_id: targetId, merged_statement: mergedStatement }),
    ),

  split: (
    id: number,
    parts: { statement: string; rationale?: string; type?: RequirementType; priority?: Priority; category?: string }[],
  ) => request<{ original_ref: string; parts: RequirementDetail[] }>(`/register/${id}/split`, json({ parts })),

  bulk: (engagementId: number, ids: number[], set: Record<string, string | string[] | null>) =>
    request<{ updated: string[]; skipped: { id: number; ref?: string; reason: string }[] }>(
      '/register/bulk',
      json({ engagement_id: engagementId, ids, set }),
    ),

  /** What would go with this requirement, and why it might refuse to. */
  deletionPlan: (id: number) => request<DeletionPlan>(`/register/${id}/deletion-plan`),

  /** Hard delete. The server recomputes the plan; a refusal comes back as a 409 whose message
   *  names the alternative (descope through a change request). */
  remove: (id: number) => request<DeletionResult>(`/register/${id}`, del()),

  /** Bulk delete, plan recomputed per row. dry_run builds the confirm dialog; the live call
   *  reports both what went and what was refused, with the reason for each. */
  bulkDelete: (engagementId: number, ids: number[], dryRun = false) =>
    request<BulkDeleteResult>('/register/bulk-delete', json({ engagement_id: engagementId, ids, dry_run: dryRun })),

  search: (engagementId: number, q: string, limit = 25) =>
    request<{ count: number; hits: (RegisterRow & { score: number })[] }>(
      `/register/search${qs({ engagement_id: engagementId, q, limit })}`,
    ),

  coverage: (engagementId: number) => request<CoverageReport>(`/register/coverage${qs({ engagement_id: engagementId })}`),

  categories: (engagementId: number) =>
    request<{ category: string; count: number }[]>(`/register/categories${qs({ engagement_id: engagementId })}`),

  /** The universal category scheme (read-only — descriptions feed tooltips/suggestions). */
  categoryScheme: (engagementId: number) =>
    request<CategorySchemeResponse>(`/register/category-scheme${qs({ engagement_id: engagementId })}`),

  /** The tag vocabulary the AGENT may draw from (operators are never pool-restricted). */
  tagPool: (engagementId: number) =>
    request<TagPoolResponse>(`/register/tag-pool${qs({ engagement_id: engagementId })}`),

  /** pool: null clears it. Server validates 0–30 lowercase-kebab tags, 2–30 chars, unique. */
  saveTagPool: (engagementId: number, poolTags: string[] | null) =>
    request<TagPoolResponse>('/register/tag-pool', put({ engagement_id: engagementId, pool: poolTags })),

  /**
   * Span in context. The Documents slice owns /api/documents/:id/span; fall back to the
   * register's own evidence-context route so provenance click-through always works.
   */
  evidenceContext: async (evidence: EvidenceRow): Promise<SpanContext> => {
    const start = evidence.locator?.char_start;
    const end = evidence.locator?.char_end;
    if (typeof start === 'number' && typeof end === 'number') {
      try {
        return await request<SpanContext>(
          `/documents/${evidence.document_id}/span${qs({ char_start: start, char_end: end, window: 700 })}`,
        );
      } catch {
        /* documents slice not available — fall through */
      }
    }
    return request<SpanContext>(`/register/evidence/${evidence.id}/context${qs({ window: 700 })}`);
  },

  validateEvidence: (engagementId: number, evidence: EvidenceInput[]) =>
    request<{ ok: boolean; failures?: EvidenceFailure[] }>(
      '/register/validate-evidence',
      json({ engagement_id: engagementId, evidence }),
    ),

  /**
   * ADD one tag to a selection — the axis the bulk bar never had, and 47 of the register's 115
   * recorded mutations.
   *
   * `bulk` takes `tags` as a FULL REPLACEMENT SET, so "add" is a grouping problem, not a missing
   * endpoint: group the selection by the tag array each row would end up with, and issue one call
   * per group. A homogeneous selection is one call; the worst case is one per row. Rows that
   * already carry the tag are left alone entirely — a no-op write is still an audit row.
   *
   * Returns the shared `{done, refused}` voice (F7.1) plus the call count, so the toast can state
   * what the convenience cost.
   */
  addTag: async (
    engagementId: number,
    rows: { id: number; ref: string; tags: string[] }[],
    tag: string,
  ): Promise<{ done: string[]; refused: { ref: string; reason: string }[]; calls: number; already: string[] }> => {
    const already = rows.filter((r) => (r.tags ?? []).includes(tag)).map((r) => r.ref);
    const groups = new Map<string, { next: string[]; ids: number[] }>();
    for (const r of rows) {
      if ((r.tags ?? []).includes(tag)) continue;
      const next = [...new Set([...(r.tags ?? []), tag])].sort();
      const key = next.join('');
      const group = groups.get(key) ?? { next, ids: [] };
      group.ids.push(r.id);
      groups.set(key, group);
    }
    const done: string[] = [];
    const refused: { ref: string; reason: string }[] = [];
    for (const group of groups.values()) {
      const res = await registerApi.bulk(engagementId, group.ids, { tags: group.next });
      done.push(...res.updated);
      for (const s of res.skipped) refused.push({ ref: s.ref ?? String(s.id), reason: s.reason });
    }
    return { done, refused, calls: groups.size, already };
  },
};

// ───────────────────────── deliverables / ACs / TCs ─────────────────────────

export const deliverablesApi = {
  list: (engagementId: number) => request<Deliverable[]>(`/deliverables${qs({ engagement_id: engagementId })}`),

  get: (id: number) => request<Deliverable>(`/deliverables/${id}`),

  create: (
    engagementId: number,
    body: {
      name: string;
      description?: string;
      definition_of_done?: string;
      status?: string;
      milestone_id?: number | null;
      requirement_ids?: number[];
    },
  ) => request<Deliverable>('/deliverables', json({ engagement_id: engagementId, ...body })),

  update: (
    id: number,
    body: Partial<{
      name: string;
      description: string;
      definition_of_done: string;
      status: string;
      /** null detaches the deliverable from its milestone. */
      milestone_id: number | null;
      /** the roadmap: YYYY-MM-DD, or null to undate */
      due_date: string | null;
      start_date: string | null;
      /** the roadmap row this card is pinned to; null lets it pack automatically again */
      roadmap_lane: number | null;
    }>,
  ) => request<Deliverable>(`/deliverables/${id}`, patch(body)),

  setStatus: (id: number, status: string) => request<Deliverable>(`/deliverables/${id}/status`, json({ status })),

  /** The milestone is what turns a deliverable into an invoice line and a G5 check. */
  setMilestone: (id: number, milestoneId: number | null) =>
    request<Deliverable>(`/deliverables/${id}`, patch({ milestone_id: milestoneId })),

  /** ── the workbench: a deliverable's content items ── */
  items: (id: number) => request<DeliverableItem[]>(`/deliverables/${id}/items`),
  addItem: (id: number, body: { title: string; spec?: string | null; due_date?: string | null; provenance?: string | null }) =>
    request<DeliverableItem>(`/deliverables/${id}/items`, json(body)),
  updateItem: (
    itemId: number,
    body: Partial<Pick<DeliverableItem, 'title' | 'spec' | 'due_date' | 'status' | 'document_ids' | 'requirement_ids' | 'sort_order'>>,
  ) => request<DeliverableItem>(`/deliverables/items/${itemId}`, patch(body)),
  removeItem: (itemId: number) => request<{ deleted: boolean }>(`/deliverables/items/${itemId}`, { method: 'DELETE' }),

  mapRequirement: (id: number, requirementId: number) =>
    request<Deliverable>(`/deliverables/${id}/requirements`, json({ requirement_id: requirementId })),

  /** Map many at once; `replace` makes the array the complete mapping for the deliverable. */
  mapRequirements: (id: number, requirementIds: number[], replace = false) =>
    request<Deliverable>(`/deliverables/${id}/requirements`, json({ requirement_ids: requirementIds, replace })),

  unmapRequirement: (id: number, requirementId: number) =>
    request<Deliverable>(`/deliverables/${id}/requirements/${requirementId}`, { method: 'DELETE' }),

  /** Every place in the register's prose that names `ref` — derived, never stored. */
  references: (engagementId: number, ref: string) =>
    request<{ engagement_id: number; ref: string; references: ProseReference[] }>(
      `/deliverables/references${qs({ engagement_id: engagementId, ref })}`,
    ),

  /** ── the dependency linker: this deliverable builds on that one ── */
  link: (id: number, body: { to_deliverable_id: number; from_item_id?: number | null; to_item_id?: number | null; note?: string | null }) =>
    request<Deliverable>(`/deliverables/${id}/links`, json(body)),
  unlink: (id: number, linkId: number) =>
    request<Deliverable>(`/deliverables/${id}/links/${linkId}`, { method: 'DELETE' }),
  /** "I looked, we are reconciled" — clears the stale flag; no record moves. */
  confirmLink: (id: number, linkId: number) =>
    request<Deliverable>(`/deliverables/${id}/links/${linkId}/confirm`, json({})),

  deletionPlan: (id: number) => request<DeletionPlan>(`/deliverables/${id}/deletion-plan`),

  /** Hard delete. Refused once a signed acceptance record covers it — 'dropped' is the retire. */
  remove: (id: number) => request<DeletionResult>(`/deliverables/${id}`, del()),
};

export const acsApi = {
  list: (engagementId: number, requirementId?: number) =>
    request<AcceptanceCriterion[]>(`/deliverables/acs${qs({ engagement_id: engagementId, requirement_id: requirementId })}`),

  create: (requirementId: number, statement: string, measurementMethod?: string) =>
    request<AcceptanceCriterion>(
      '/deliverables/acs',
      json({ requirement_id: requirementId, statement, measurement_method: measurementMethod }),
    ),

  update: (id: number, body: { statement?: string; measurement_method?: string }) =>
    request<AcceptanceCriterion>(`/deliverables/acs/${id}`, patch(body)),

  setStatus: (id: number, status: string) => request<AcceptanceCriterion>(`/deliverables/acs/${id}/status`, json({ status })),

  lint: (statement: string, measurementMethod?: string, requirementType?: string) =>
    request<LintResult>(
      '/deliverables/acs/lint',
      json({ statement, measurement_method: measurementMethod, requirement_type: requirementType }),
    ),

  deletionPlan: (id: number) => request<DeletionPlan>(`/deliverables/acs/${id}/deletion-plan`),

  /** Hard delete. Refused while a defect cites it, or when its requirement is signed scope. */
  remove: (id: number) => request<DeletionResult & { requirement_id: number | null }>(`/deliverables/acs/${id}`, del()),
};

export const testCasesApi = {
  list: (engagementId: number) => request<TestCase[]>(`/deliverables/test-cases${qs({ engagement_id: engagementId })}`),

  create: (
    engagementId: number,
    body: {
      title: string;
      preconditions?: string;
      test_data_ref?: string;
      expected_result?: string;
      steps?: { action: string; expected: string }[];
      acceptance_criterion_ids?: number[];
    },
  ) => request<TestCase>('/deliverables/test-cases', json({ engagement_id: engagementId, ...body })),

  update: (
    id: number,
    body: Partial<{
      title: string;
      preconditions: string;
      test_data_ref: string;
      expected_result: string;
      steps: { action: string; expected: string }[];
      status: string;
    }>,
  ) => request<TestCase>(`/deliverables/test-cases/${id}`, patch(body)),

  /** draft → ready is what publishes a test case to the client's UAT list. */
  setStatus: (id: number, status: string) => request<TestCase>(`/deliverables/test-cases/${id}/status`, json({ status })),

  bulkStatus: (engagementId: number, ids: number[], status: string) =>
    request<{ updated: string[]; skipped: { id: number; reason: string }[]; status: string }>(
      '/deliverables/test-cases/bulk-status',
      json({ engagement_id: engagementId, ids, status }),
    ),

  linkAc: (id: number, acceptanceCriterionId: number) =>
    request<TestCase>(`/deliverables/test-cases/${id}/acs`, json({ acceptance_criterion_id: acceptanceCriterionId })),

  unlinkAc: (id: number, acceptanceCriterionId: number) =>
    request<TestCase>(`/deliverables/test-cases/${id}/acs/${acceptanceCriterionId}`, { method: 'DELETE' }),

  deletionPlan: (id: number) => request<DeletionPlan>(`/deliverables/test-cases/${id}/deletion-plan`),

  /** Hard delete. Refused once the client has recorded a run against it — 'dropped' is the retire. */
  remove: (id: number) => request<DeletionResult>(`/deliverables/test-cases/${id}`, del()),
};

/** CSV downloads go through the browser so the session cookie and Content-Disposition apply. */
export const exportsApi = {
  registerCsvUrl: (engagementId: number) => `/api/exports/register.csv${qs({ engagement_id: engagementId })}`,
  traceabilityCsvUrl: (engagementId: number) => `/api/exports/traceability.csv${qs({ engagement_id: engagementId })}`,
};
